import React from 'react';
import {
    Clock, Trophy, Flame,
    Zap, Star, TrendingUp, Calendar, Swords, Award,
    Loader2, ChevronDown, ChevronUp
} from 'lucide-react';

const SessionDetails = ({
    sessions,
    loading,
    formatDate,
    formatDuration,
    showRank = false,
    showPoints = false,
    onSessionClick
}) => {
    if (loading) {
        return (
            <div className="session-details loading">
                <Loader2 size={16} className="spin" />
                <span>Ladataan sessioita...</span>
            </div>
        );
    }

    if (!sessions || sessions.length === 0) {
        return (
            <div className="session-details empty">
                <span>Ei sessiotietoja</span>
            </div>
        );
    }

    const clickable = typeof onSessionClick === 'function';

    return (
        <div className="session-details">
            {clickable && (
                <p className="session-details-hint">Klikkaa peliä nähdäksesi tiedot.</p>
            )}
            <div className="session-list">
                {sessions.map((session, idx) => {
                    const sid = session.sessionId;
                    const canOpen = clickable && sid != null;
                    return (
                        <div
                            key={sid || idx}
                            className={`session-item${canOpen ? ' session-item-clickable' : ''}`}
                            onClick={
                                canOpen
                                    ? (e) => {
                                          e.stopPropagation();
                                          onSessionClick(sid);
                                      }
                                    : undefined
                            }
                            role={canOpen ? 'button' : undefined}
                            tabIndex={canOpen ? 0 : undefined}
                            onKeyDown={
                                canOpen
                                    ? (e) => {
                                          if (e.key === 'Enter' || e.key === ' ') {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              onSessionClick(sid);
                                          }
                                      }
                                    : undefined
                            }
                        >
                            <span className="session-date">{formatDate(session.date)}</span>
                            <span className="session-game">{session.gameName}</span>
                            {session.durationMinutes > 0 && (
                                <span className="session-duration">{formatDuration(session.durationMinutes)}</span>
                            )}
                            {showRank && session.rank && (
                                <span className={`session-rank rank-${session.rank}`}>
                                    #{session.rank}
                                </span>
                            )}
                            {showPoints && (session.leaguePoints !== null && session.leaguePoints !== undefined) && (
                                <span className="session-points">
                                    {session.leaguePoints} MP
                                </span>
                            )}
                            {session.winner && (
                                <span className="session-winner">🏆 {session.winner}</span>
                            )}
                            {session.players && (
                                <span className="session-players">{session.players}</span>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const StatSection = ({ title, subtitle, icon, children, isExpanded, onToggle }) => (
    <div className="stat-section">
        <div className="stat-section-header" onClick={onToggle}>
            <div className="stat-section-title">
                {icon}
                <div>
                    <h3>{title}</h3>
                    <small>{subtitle}</small>
                </div>
            </div>
            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
        {isExpanded && (
            <div className="stat-section-content">
                {children}
            </div>
        )}
    </div>
);

/**
 * Renders analytics blocks in admin-configured order (visibility + order).
 */
export default function StatisticsAnalyticsSections({
    stats,
    orderedSections,
    expandedSections,
    toggleSection,
    expandedRows,
    rowSessions,
    loadingRow,
    toggleRowExpansion,
    formatDate,
    formatDuration,
    onSessionClick
}) {
    const expanded = (id) => expandedSections[id] === true;

    const renderSection = (sectionId) => {
        if (!stats) return null;

        switch (sectionId) {
            case 'longestGames':
                return (
                    <StatSection
                        title="Pisimmät pelit"
                        subtitle="Eniten aikaa käytetty"
                        icon={<Clock size={20} />}
                        isExpanded={expanded('longestGames')}
                        onToggle={() => toggleSection('longestGames')}
                    >
                        <div className="stats-table">
                            <div className="table-header">
                                <span className="col-rank">#</span>
                                <span className="col-game">Peli</span>
                                <span className="col-stat">Yhteensä</span>
                                <span className="col-stat">Kerrat</span>
                                <span className="col-stat">Keskiarvo</span>
                            </div>
                            {stats?.longestGames?.map((game, idx) => {
                                const rowKey = `longest-${game.gameId}`;
                                const isRowExpanded = expandedRows[rowKey];
                                const sessions = rowSessions[rowKey];
                                return (
                                    <React.Fragment key={game.gameId}>
                                        <div
                                            className={`table-row clickable ${isRowExpanded ? 'expanded' : ''}`}
                                            onClick={() => toggleRowExpansion(rowKey, 'longestGame', game.gameId)}
                                        >
                                            <span className="col-rank rank-badge">{idx + 1}</span>
                                            <span className="col-game">
                                                {game.gameImage && (
                                                    <img src={game.gameImage} alt="" className="game-thumb" />
                                                )}
                                                {game.gameName}
                                                <ChevronDown size={14} className={`expand-icon ${isRowExpanded ? 'rotated' : ''}`} />
                                            </span>
                                            <span className="col-stat highlight">{formatDuration(game.totalMinutes)}</span>
                                            <span className="col-stat">{game.sessionCount}x</span>
                                            <span className="col-stat">{formatDuration(Math.round(game.avgMinutes))}</span>
                                        </div>
                                        {isRowExpanded && (
                                            <SessionDetails
                                                sessions={sessions}
                                                loading={loadingRow === rowKey}
                                                formatDate={formatDate}
                                                formatDuration={formatDuration}
                                                onSessionClick={onSessionClick}
                                            />
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </StatSection>
                );

            case 'mostPlayed':
                return (
                    <StatSection
                        title="Suosituimmat pelit"
                        subtitle="Eniten pelattuja"
                        icon={<Star size={20} />}
                        isExpanded={expanded('mostPlayed')}
                        onToggle={() => toggleSection('mostPlayed')}
                    >
                        <div className="stats-table">
                            <div className="table-header">
                                <span className="col-rank">#</span>
                                <span className="col-game">Peli</span>
                                <span className="col-stat">Pelattu</span>
                                <span className="col-stat">Pelaajia</span>
                            </div>
                            {stats?.mostPlayedGames?.map((game, idx) => {
                                const rowKey = `played-${game.gameId}`;
                                const isRowExpanded = expandedRows[rowKey];
                                const sessions = rowSessions[rowKey];
                                return (
                                    <React.Fragment key={game.gameId}>
                                        <div
                                            className={`table-row clickable ${isRowExpanded ? 'expanded' : ''}`}
                                            onClick={() => toggleRowExpansion(rowKey, 'mostPlayed', game.gameId)}
                                        >
                                            <span className="col-rank rank-badge">{idx + 1}</span>
                                            <span className="col-game">
                                                {game.gameImage && (
                                                    <img src={game.gameImage} alt="" className="game-thumb" />
                                                )}
                                                {game.gameName}
                                                <ChevronDown size={14} className={`expand-icon ${isRowExpanded ? 'rotated' : ''}`} />
                                            </span>
                                            <span className="col-stat highlight">{game.playCount}x</span>
                                            <span className="col-stat">{game.uniquePlayers}</span>
                                        </div>
                                        {isRowExpanded && (
                                            <SessionDetails
                                                sessions={sessions}
                                                loading={loadingRow === rowKey}
                                                formatDate={formatDate}
                                                formatDuration={formatDuration}
                                                onSessionClick={onSessionClick}
                                            />
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </StatSection>
                );

            case 'winRates':
                return (
                    <StatSection
                        title="Voittoprosentit"
                        subtitle="Vähintään 3 peliä"
                        icon={<Trophy size={20} />}
                        isExpanded={expanded('winRates')}
                        onToggle={() => toggleSection('winRates')}
                    >
                        <div className="stats-table">
                            <div className="table-header">
                                <span className="col-rank">#</span>
                                <span className="col-game">Pelaaja</span>
                                <span className="col-stat">Voitto-%</span>
                                <span className="col-stat">Voitot</span>
                                <span className="col-stat">Pelejä</span>
                                <span className="col-stat">Kesk.sij.</span>
                            </div>
                            {stats?.playerWinRates?.map((player, idx) => {
                                const rowKey = `winrate-${player.playerId}`;
                                const isRowExpanded = expandedRows[rowKey];
                                const sessions = rowSessions[rowKey];
                                return (
                                    <React.Fragment key={player.playerId}>
                                        <div
                                            className={`table-row clickable ${isRowExpanded ? 'expanded' : ''}`}
                                            onClick={() => toggleRowExpansion(rowKey, 'playerWins', null, player.playerId)}
                                        >
                                            <span className="col-rank rank-badge">{idx + 1}</span>
                                            <span className="col-game">
                                                {player.playerName}
                                                <ChevronDown size={14} className={`expand-icon ${isRowExpanded ? 'rotated' : ''}`} />
                                            </span>
                                            <span className="col-stat highlight">{player.winRate}%</span>
                                            <span className="col-stat">{player.wins}</span>
                                            <span className="col-stat">{player.gamesPlayed}</span>
                                            <span className="col-stat">{player.avgRank?.toFixed(1)}</span>
                                        </div>
                                        {isRowExpanded && (
                                            <SessionDetails
                                                sessions={sessions}
                                                loading={loadingRow === rowKey}
                                                formatDate={formatDate}
                                                formatDuration={formatDuration}
                                                showRank={true}
                                                onSessionClick={onSessionClick}
                                            />
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </StatSection>
                );

            case 'winStreaks':
                if (!stats?.winStreaks?.length) return null;
                return (
                    <StatSection
                        title="Voittoputket"
                        subtitle="Peräkkäiset voitot"
                        icon={<Flame size={20} />}
                        isExpanded={expanded('winStreaks')}
                        onToggle={() => toggleSection('winStreaks')}
                    >
                        <div className="streak-cards">
                            {stats.winStreaks.map((streak, idx) => (
                                <div key={idx} className="streak-card">
                                    <div className="streak-flames">
                                        {Array.from({ length: Math.min(streak.streakLength, 5) }).map((_, i) => (
                                            <Flame key={i} size={16} className="flame" />
                                        ))}
                                    </div>
                                    <div className="streak-name">{streak.playerName}</div>
                                    <div className="streak-count">{streak.streakLength} voittoa peräkkäin</div>
                                    <div className="streak-dates">
                                        {formatDate(streak.streakStart)} - {formatDate(streak.streakEnd)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </StatSection>
                );

            case 'highestScores':
                if (!stats?.highestScores?.length) return null;
                return (
                    <StatSection
                        title="Korkeimmat pisteet"
                        subtitle="Top 10 suoritukset"
                        icon={<Zap size={20} />}
                        isExpanded={expanded('highestScores')}
                        onToggle={() => toggleSection('highestScores')}
                    >
                        <div className="stats-table">
                            <div className="table-header">
                                <span className="col-rank">#</span>
                                <span className="col-game">Pelaaja</span>
                                <span className="col-game">Peli</span>
                                <span className="col-stat">Pisteet</span>
                                <span className="col-stat">Pvm</span>
                            </div>
                            {stats.highestScores.map((score, idx) => (
                                <div key={idx} className="table-row">
                                    <span className="col-rank rank-badge">{idx + 1}</span>
                                    <span className="col-game">{score.playerName}</span>
                                    <span className="col-game">{score.gameName}</span>
                                    <span className="col-stat highlight">{score.score}</span>
                                    <span className="col-stat">{formatDate(score.date)}</span>
                                </div>
                            ))}
                        </div>
                    </StatSection>
                );

            case 'closestGames':
                if (!stats?.closestGames?.length) return null;
                return (
                    <StatSection
                        title="Tasaisimmat pelit"
                        subtitle="Pienin ero voittajan ja kakkosen välillä"
                        icon={<Swords size={20} />}
                        isExpanded={expanded('closestGames')}
                        onToggle={() => toggleSection('closestGames')}
                    >
                        <div className="stats-table compact">
                            {stats.closestGames.slice(0, 5).map((game, idx) => (
                                <div key={idx} className="table-row closest-row">
                                    <span className="game-info">
                                        <strong>{game.gameName}</strong>
                                        <small>{formatDate(game.date)}</small>
                                    </span>
                                    <span className="matchup">
                                        <span className="winner">{game.winnerName} ({game.winnerScore})</span>
                                        <span className="vs">vs</span>
                                        <span className="runnerup">{game.runnerupName} ({game.runnerupScore})</span>
                                    </span>
                                    <span className="diff">Δ {game.scoreDiff}</span>
                                </div>
                            ))}
                        </div>
                    </StatSection>
                );

            case 'playerFavorites':
                if (!stats?.playerFavorites?.length) return null;
                return (
                    <StatSection
                        title="Pelaajien suosikit"
                        subtitle="Eniten pelattu peli per pelaaja"
                        icon={<Award size={20} />}
                        isExpanded={expanded('playerFavorites')}
                        onToggle={() => toggleSection('playerFavorites')}
                    >
                        <div className="favorites-grid">
                            {stats.playerFavorites.map((fav, idx) => (
                                <div key={idx} className="favorite-card">
                                    <div className="fav-player">{fav.playerName}</div>
                                    <div className="fav-game">
                                        {fav.gameImage && (
                                            <img src={fav.gameImage} alt="" className="fav-img" />
                                        )}
                                        <span>{fav.favoritGame}</span>
                                    </div>
                                    <div className="fav-stats">
                                        {fav.playCount}x pelattu • {fav.winsInGame} voittoa
                                    </div>
                                </div>
                            ))}
                        </div>
                    </StatSection>
                );

            case 'monthlyActivity':
                if (!stats?.monthlyActivity?.length) return null;
                return (
                    <StatSection
                        title="Kuukausiaktiivisuus"
                        subtitle="Pelaamisen trendi"
                        icon={<Calendar size={20} />}
                        isExpanded={expanded('monthlyActivity')}
                        onToggle={() => toggleSection('monthlyActivity')}
                    >
                        <div className="activity-chart">
                            {stats.monthlyActivity.slice(0, 12).map((month, idx) => {
                                const maxSessions = Math.max(...stats.monthlyActivity.slice(0, 12).map((m) => m.sessionCount));
                                const height = maxSessions ? (month.sessionCount / maxSessions) * 100 : 0;
                                return (
                                    <div key={idx} className="bar-container">
                                        <div
                                            className="bar"
                                            style={{ height: `${height}%` }}
                                            title={`${month.sessionCount} sessiota`}
                                        >
                                            <span className="bar-value">{month.sessionCount}</span>
                                        </div>
                                        <span className="bar-label">{month.month}/{String(month.year).slice(2)}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </StatSection>
                );

            case 'avgPointsPerGame':
                if (!stats?.avgPointsPerGame?.length) return null;
                return (
                    <StatSection
                        title="Keskimääräinen menestyspisteansio"
                        subtitle="Johtotaulukko per pelattu peli"
                        icon={<Trophy size={20} />}
                        isExpanded={expanded('avgPointsPerGame')}
                        onToggle={() => toggleSection('avgPointsPerGame')}
                    >
                        <div className="stats-table">
                            <div className="table-header">
                                <span className="col-rank">#</span>
                                <span className="col-game">Pelaaja</span>
                                <span className="col-stat">Keskimäärä</span>
                                <span className="col-stat">Yhteensä</span>
                                <span className="col-stat">Pelejä</span>
                            </div>
                            {stats.avgPointsPerGame.map((player, idx) => {
                                const rowKey = `avgpoints-${player.playerId}`;
                                const isRowExpanded = expandedRows[rowKey];
                                const sessions = rowSessions[rowKey];
                                return (
                                    <React.Fragment key={player.playerId}>
                                        <div
                                            className={`table-row clickable ${isRowExpanded ? 'expanded' : ''}`}
                                            onClick={() => toggleRowExpansion(rowKey, 'playerWins', null, player.playerId)}
                                        >
                                            <span className="col-rank rank-badge">{idx + 1}</span>
                                            <span className="col-game">
                                                {player.playerName}
                                                <ChevronDown size={14} className={`expand-icon ${isRowExpanded ? 'rotated' : ''}`} />
                                            </span>
                                            <span className="col-stat highlight">{parseFloat(player.avgPointsPerGame).toFixed(2)}</span>
                                            <span className="col-stat">{player.totalPoints}</span>
                                            <span className="col-stat">{player.gamesPlayed}</span>
                                        </div>
                                        {isRowExpanded && (
                                            <SessionDetails
                                                sessions={sessions}
                                                loading={loadingRow === rowKey}
                                                formatDate={formatDate}
                                                formatDuration={formatDuration}
                                                showRank={true}
                                                showPoints={true}
                                                onSessionClick={onSessionClick}
                                            />
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </StatSection>
                );

            case 'headToHead':
                if (!stats?.headToHead?.length) return null;
                return (
                    <StatSection
                        title="Keskinäiset kohtaamiset"
                        subtitle="Kuka voittaa ketä useimmin"
                        icon={<TrendingUp size={20} />}
                        isExpanded={expanded('headToHead')}
                        onToggle={() => toggleSection('headToHead')}
                    >
                        <div className="h2h-list">
                            {stats.headToHead.slice(0, 10).map((matchup, idx) => (
                                <div key={idx} className="h2h-row">
                                    <span className="h2h-winner">{matchup.winnerName}</span>
                                    <span className="h2h-score">{matchup.timesBeaten}x</span>
                                    <span className="h2h-loser">{matchup.loserName}</span>
                                </div>
                            ))}
                        </div>
                    </StatSection>
                );

            default:
                return null;
        }
    };

    return (
        <>
            {orderedSections.map((section) => (
                <React.Fragment key={section.id}>
                    {renderSection(section.id)}
                </React.Fragment>
            ))}
        </>
    );
}
